import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Boxes,
  Building2,
  Camera,
  Clock,
  Download,
  DollarSign,
  HardHat,
  Layers,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Snowflake,
  Smartphone,
  Truck,
  Users,
} from 'lucide-react';
import { Badge, Button, Card } from '../components/ui.jsx';
import { useApi } from '../lib/hooks.js';
import { countOf, dollars } from '../lib/plain.js';
import BrandMark from '../components/BrandMark.jsx';

const PHONE = '1-855-232-7996';
const PHONE_HREF = 'tel:+18552327996';
const SUPPORT = 'support@3lumpers.com';

const SERVICES = [
  {
    icon: Boxes,
    title: 'Floor loads',
    body: 'Boxes stacked to the roof with no pallets. We take them off by hand and stack them so your receivers can move them.',
  },
  {
    icon: Layers,
    title: 'Pallet breakdown',
    body: 'Mixed and rainbow pallets separated out by product, lot or aisle before they go into your racking.',
  },
  {
    icon: Snowflake,
    title: 'Produce and cold trailers',
    body: 'Reefer and frozen trailers unloaded quickly with the doors closed as little as possible.',
  },
  {
    icon: Truck,
    title: 'Container devanning',
    body: 'Hand-unloading sea containers onto pallets, sorted and shrink-wrapped ready for storage.',
  },
  {
    icon: Clock,
    title: 'Late and weekend crews',
    body: 'Trailers that turn up at 2am still get unloaded. Night and weekend crews on standby.',
  },
  {
    icon: ShieldCheck,
    title: 'Load shift and distressed loads',
    body: 'Freight that moved in transit or needs re-stacking so the rest can be delivered safely.',
  },
];

const STEPS = [
  ['Tell us the job', 'What needs unloading, how many pallets and when. About a minute.'],
  ['We send someone', 'Lumpers near the dock get it on their phone. Most jobs are taken within the hour.'],
  ['See it through', 'You watch them arrive, start and finish, with photos and the pallet count as proof.'],
];

export default function Landing() {
  const { data: stats } = useApi('/public/stats');

  useEffect(() => {
    document.title = '3Lumpers — Lumper labor for warehouses and workers';
  }, []);

  return (
    <div className="landing">
      <div className="util-bar">
        <div className="util-bar-inner">
          <a href={PHONE_HREF} className="row gap-6">
            <Phone size={13} /> {PHONE}
          </a>
          <span className="dimmer hide-sm">·</span>
          <span className="dim hide-sm">Crews across South Florida</span>
          <div className="grow" />
          <Link to="/login" className="row gap-6">
            Log in
          </Link>
          <Link to="/signup" className="hide-sm">
            <Button size="sm" variant="primary" className="pill-btn">Sign up</Button>
          </Link>
        </div>
      </div>

      <header className="masthead">
        <div className="masthead-inner">
          <Link to="/" className="row gap-10">
            <BrandMark size={40} />
            <span className="brand-text">
              <strong>3Lumpers</strong>
              <span>Lumper labor</span>
            </span>
          </Link>
          <nav className="masthead-links">
            <a href="#lumpers">Lumpers</a>
            <a href="#warehouses">Warehouses</a>
            <a href="#how">How it works</a>
            <a href="#services">Services</a>
          </nav>
          <div className="grow" />
          <Link to="/get-the-app" className="row gap-8 hide-sm">
            <Smartphone size={15} className="amber" />
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>Get the app</span>
          </Link>
          <Link to="/signup">
            <Button variant="primary" className="pill-btn" trailing={<ArrowRight size={14} />}>
              Sign up
            </Button>
          </Link>
        </div>
      </header>

      {/* The two doors, sitting on the photo the way Labor Loop does it. */}
      <section className="hero-photo tall">
        <div className="hero-copy">
          <h1 className="hero-title">
            Trailers unloaded
            <br />
            by hand. Tonight.
          </h1>
          <div className="hero-rule" />
          <p className="hero-sub">
            Floor loads, pallets, produce and containers — unloaded by crews we know, at docks across
            South Florida.
          </p>

          <div className="doors">
            <Link to="/signup?path=lumper" className="door">
              <Badge tone="hazard" size="sm" className="nowrap" style={{ alignSelf: 'flex-start' }}>
                <HardHat size={12} /> For workers
              </Badge>
              <h2>Are you a lumper?</h2>
              <p>
                Get unloading work on your phone. See the pay, the pallets and how far it is before you
                say yes. Paid every Friday.
              </p>
              <div className="door-foot">
                <Button variant="primary" size="lg" className="btn-block" trailing={<ArrowRight size={15} />}>
                  Sign up now
                </Button>
                <div className="row gap-8 mt-12 wrap">
                  <span className="chip">iPhone &amp; Android</span>
                  <span className="chip">No experience needed</span>
                </div>
              </div>
            </Link>

            <Link to="/signup?path=business" className="door">
              <Badge tone="amber" size="sm" className="nowrap" style={{ alignSelf: 'flex-start' }}>
                <Building2 size={12} /> For businesses
              </Badge>
              <h2>Are you a business that needs lumper labor?</h2>
              <p>
                Post the job and we send a crew. Photos, pallet counts and one clean invoice — no
                dispatch calls to chase.
              </p>
              <div className="door-foot">
                <Button variant="primary" size="lg" className="btn-block" trailing={<ArrowRight size={15} />}>
                  Sign up now
                </Button>
                <div className="row gap-8 mt-12 wrap">
                  <span className="chip">No contracts</span>
                  <span className="chip">Insured crews</span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="help-band">
        <h2>Need help unloading freight?</h2>
        <p>
          Tell us what is turning up and when. We pair experienced lumpers with the warehouses, brokers
          and carriers that need them — and we keep the pallet count honest on both sides.
        </p>
        <div className="row gap-12 mt-20 wrap" style={{ justifyContent: 'center' }}>
          <Link to="/signup?path=business">
            <Button variant="primary" size="lg">Get a crew</Button>
          </Link>
          <Link to="/signup?path=lumper">
            <Button variant="outline" size="lg">Find work</Button>
          </Link>
        </div>
        <div className="mt-24">
          <div className="label" style={{ textAlign: 'center' }}>Contact us</div>
          <div className="row gap-20 wrap mt-12" style={{ justifyContent: 'center' }}>
            <a href={PHONE_HREF} className="row gap-8" style={{ fontSize: 'var(--fs-lg)', fontWeight: 650 }}>
              <Phone size={17} className="amber" />
              {PHONE}
            </a>
            <a href={`mailto:${SUPPORT}`} className="row gap-8" style={{ fontSize: 'var(--fs-lg)', fontWeight: 650 }}>
              <Mail size={17} className="amber" />
              {SUPPORT}
            </a>
          </div>
          <div className="dim mt-8" style={{ fontSize: 'var(--fs-sm)', textAlign: 'center' }}>
            Phone 6am to 8pm, every day. Email any time.
          </div>
        </div>
      </section>

      <section className="landing-section" id="services">
        <header>
          <span className="label">What we unload</span>
          <h2 className="mt-8">If it is in a trailer, we will get it out.</h2>
        </header>
        <div className="card-grid">
          {SERVICES.map((service) => (
            <article key={service.title} className="svc-card">
              <span className="svc-icon">
                <service.icon size={20} strokeWidth={2} />
              </span>
              <h3>{service.title}</h3>
              <p>{service.body}</p>
              <Link to="/signup?path=business" className="learn-more">Get a crew →</Link>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-section" id="how">
        <header>
          <span className="label">How it works</span>
          <h2 className="mt-8">Three steps, no phone tag.</h2>
        </header>
        <div className="card-grid">
          {STEPS.map(([title, body], index) => (
            <article key={title} className="svc-card">
              <span className="step-num">{index + 1}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-section" id="lumpers">
        <div className="split">
          <div>
            <span className="label">For lumpers · the 3Lumpers app</span>
            <h2 className="mt-8" id="the-app">Work when you want it. Paid on Friday.</h2>
            <p className="lede">
              Sign up once, then work comes to your phone. Accept what suits you, ignore the rest — saying
              no never counts against you.
            </p>
            <div className="col gap-12 mt-20">
              {[
                'Every offer shows the pay, the pallets and the distance first',
                'One tap at the dock starts the clock — no paperwork on site',
                'Direct deposit every Friday, fast pay if you need it sooner',
                'Ask to rate the job and the warehouse rates you back',
              ].map((line) => (
                <span key={line} className="row gap-10 muted">
                  <ShieldCheck size={15} style={{ color: 'var(--green)', flex: '0 0 auto' }} />
                  {line}
                </span>
              ))}
            </div>
            <div className="row gap-12 mt-28 wrap">
              <Link to="/get-the-app">
                <Button variant="primary" size="lg" icon={Download}>Get the app</Button>
              </Link>
              <Link to="/signup?path=lumper">
                <Button variant="outline" size="lg" icon={HardHat}>Sign up to work</Button>
              </Link>
              <a href={PHONE_HREF}>
                <Button variant="outline" size="lg" icon={Phone}>{PHONE}</Button>
              </a>
            </div>
          </div>

          <Card>
            <div className="card-body">
              <div className="row between mb-12">
                <span className="label">What an offer looks like</span>
                <Badge tone="red" dot>Rush</Badge>
              </div>
              <div className="job-card hot">
                <div className="job-card-head">
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)', fontWeight: 600 }}>Job 10482</span>
                  <span className="grow" />
                  <Badge tone="teal" size="sm">12 miles away</Badge>
                </div>
                <div className="job-card-site">Coastal Grocers DC</div>
                <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                  Door 14 · Medley, FL · starts 4:30 AM
                </div>
                <div className="job-card-meta">
                  <span>38 pallets</span>
                  <span>Loose boxes</span>
                  <span>Cold trailer</span>
                  <span>about 5 hours</span>
                </div>
                <div className="job-card-money">
                  <span className="pay">$268</span>
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>paid this Friday</span>
                </div>
                <div className="row gap-8 mt-12">
                  <Link to="/signup?path=lumper" className="grow">
                    <Button variant="primary" size="sm" className="btn-block">I will do it</Button>
                  </Link>
                  <Button variant="outline" size="sm">Not this one</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <section className="landing-section" id="warehouses">
        <div className="landing-cta">
          <div className="hazard-rule" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 5, borderRadius: 0 }} />
          <span className="label">Where we work</span>
          <h2 className="mt-8" style={{ fontSize: 'clamp(24px, 4vw, var(--fs-4xl))' }}>
            {countOf(stats?.activeLumpers || 0, 'lumper')} on the roster across {countOf(stats?.cities || 0, 'city', 'cities')}.
          </h2>
          <p className="muted mt-12" style={{ fontSize: 'var(--fs-lg)', maxWidth: '62ch' }}>
            We have moved {countOf(stats?.palletsMoved || 0, 'pallet')} and finished{' '}
            {countOf(stats?.jobsCompleted || 0, 'job')} for {countOf(stats?.customers || 0, 'customer')} across{' '}
            {countOf(stats?.facilities || 0, 'dock')}.
            {stats?.fillRate ? ` ${stats.fillRate}% of jobs get someone, usually within ${stats.avgFillMinutes} minutes.` : ''}
          </p>
          <div className="row gap-12 mt-20 wrap">
            <Link to="/signup?path=business">
              <Button variant="primary" size="lg" icon={Building2}>Get tonight's job covered</Button>
            </Link>
            <Link to="/signup?path=lumper">
              <Button variant="outline" size="lg" icon={HardHat}>Work with us</Button>
            </Link>
          </div>
          <div className="row gap-16 mt-24 wrap dim" style={{ fontSize: 'var(--fs-sm)' }}>
            <span className="row gap-6"><MapPin size={13} /> Miami · Fort Lauderdale · Palm Beach · Orlando</span>
            <span className="row gap-6"><Camera size={13} /> Photos on every job</span>
            <span className="row gap-6"><DollarSign size={13} /> Per pallet or per hour</span>
            <span className="row gap-6"><Users size={13} /> Crews and vendors welcome</span>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="row gap-10">
          <BrandMark size={28} />
          <span>© {new Date().getFullYear()} 3Lumpers Services LLC · Medley, Florida</span>
        </div>
        <div className="row gap-20 wrap">
          <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>
          <a href={`mailto:dispatch@3lumpers.com`}>dispatch@3lumpers.com</a>
          <a href={PHONE_HREF}>{PHONE}</a>
          <Link to="/privacy">Privacy</Link>
          <Link to="/signup" className="amber">Sign up</Link>
          <Link to="/login">Staff log in</Link>
        </div>
      </footer>
    </div>
  );
}
